import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import {
  ComponentLoader,
  DynamicComponentComponent,
  filterRenderers,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import { Patient } from '@principle-theorem/principle-core';
import {
  AppIntegrationFeature,
  IHealthcareClaim,
  IPatient,
  ITransaction,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  Firestore,
  multiMap,
  multiSwitchMap,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';
import {
  BridgeCommandsService,
  IAvailableBridgeFeature,
} from '../../../bridge-commands.service';
import { IIntegrationActionData } from '../../../integration-action-data';
import { INTEGRATION_ACTION_RENDERERS } from '../../../integration-action-renderers';

export interface ITransactionActionsData<T> {
  transaction: WithRef<ITransaction<T>>;
  invoice: WithRef<IPatient>;
  claim?: IHealthcareClaim;
}

@Component({
  selector: 'pr-integration-display',
  templateUrl: './integration-display.component.html',
  styleUrls: ['./integration-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DynamicComponentComponent],
})
export class IntegrationDisplayComponent {
  patient$ = new ReplaySubject<WithRef<IPatient>>(1);
  appIntegrationFeature$ = new ReplaySubject<AppIntegrationFeature>(1);
  trackByDefinition =
    TrackByFunctions.index<ComponentLoader<unknown, IIntegrationActionData>>();
  actionsComponents: Signal<
    ComponentLoader<unknown, IIntegrationActionData>[] | undefined
  >;

  @Input()
  set patient(patient: WithRef<IPatient>) {
    if (patient) {
      this.patient$.next(patient);
    }
  }

  @Input()
  set appIntegrationFeature(appIntegrationFeature: AppIntegrationFeature) {
    if (appIntegrationFeature) {
      this.appIntegrationFeature$.next(appIntegrationFeature);
    }
  }

  constructor(
    bridgeCommands: BridgeCommandsService,
    private _featureFlags: FeatureFlagsService
  ) {
    const patientWithReferenceId$ = this.patient$.pipe(
      concatMap((patient) => this._inheritReferenceId(patient))
    );

    this.actionsComponents = toSignal(
      combineLatest([
        patientWithReferenceId$,
        bridgeCommands.availableFeatures$,
        this.appIntegrationFeature$,
      ]).pipe(
        switchMap(([patient, availableFeatures, appIntegrationFeature]) =>
          this._resolveActionsComponentLoader$(
            patient,
            availableFeatures,
            appIntegrationFeature
          )
        )
      )
    );
  }

  private _resolveActionsComponentLoader$(
    patient: WithRef<IPatient>,
    availableFeatures: IAvailableBridgeFeature[],
    appIntegrationFeature: AppIntegrationFeature
  ): Observable<ComponentLoader<unknown, IIntegrationActionData>[]> {
    const data: IIntegrationActionData = {
      patient,
      availableFeatures,
      appIntegrationFeature,
    };
    return of(filterRenderers(INTEGRATION_ACTION_RENDERERS, data)).pipe(
      multiSwitchMap((renderer) => {
        if (!renderer.feature) {
          return of(renderer);
        }
        return this._featureFlags
          .isFeatureEnabled$(renderer.feature)
          .pipe(map((isEnabled) => (isEnabled ? renderer : undefined)));
      }),
      map(compact),
      multiMap((renderer) => ({
        component: renderer.component,
        data,
      }))
    );
  }

  private async _inheritReferenceId(
    patient: WithRef<IPatient>
  ): Promise<WithRef<IPatient>> {
    if (patient.referenceId) {
      return patient;
    }

    const relationshipRefs = Patient.relationshipRefs(patient, [
      PatientRelationshipType.DuplicatePatient,
      PatientRelationshipType.PrimaryPatient,
    ]);
    const relatedPatients = await asyncForEach(relationshipRefs, (ref) =>
      Firestore.getDoc(ref)
    );
    const referenceId = relatedPatients.find(
      (relatedPatient) => !!relatedPatient.referenceId
    )?.referenceId;

    return referenceId ? { ...patient, referenceId } : patient;
  }
}
